import axios from '@/lib/axios'
import { useCefsStore } from '@/stores/cefs'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import _ from 'lodash'
import { storeToRefs } from 'pinia'
import { type Ref, computed, watch } from 'vue'

import type { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { CefsFilters } from '../interfaces/CefFilters'
import { Cef } from '../interfaces/cef'

interface Params extends CefsFilters {}

const getCompanyCefs = async (
	params: Params,
	page?: string | number
): Promise<PaginatedResponse<Cef>> => {
	const { data } = await axios.get<PaginatedResponse<Cef>>(
		`api/companies/${params.company}/cefs?page=${page}`,
		{
			params: {
				perPage: params.perPage ?? 15,
				search: params.search === '' ? null : params.search,
				travelerName: params.travelerName === '' ? null : params.travelerName,
				confirmation: params.confirmation === '' ? null : params.confirmation,
				name: params.name === '' ? null : params.name,
				creatorName: params.creatorName === '' ? null : params.creatorName,
				company: params.company,
				cef: params.cef,
				supplierName: params.supplierName === '' ? null : params.supplierName,
				tripReason: params.tripReason ? params.tripReason : null,
				folio: params.folio,
				order: params.order === 'Todos' ? null : params.order,
				status: params.status === 0 ? null : params.status,
				progressStage:
					params.progressStage === 'Todos' ? null : params.progressStage,
				hasLetter: params.hasLetter === 'Todos' ? null : params.hasLetter,
				service: params.service === 'Todos' ? null : params.service,
				channel: params.channel === 0 ? null : params.channel,
				dates: params.dates,
				creationDates: params.creationDates,
				onlyRequests: params.onlyRequests ? true : null,
				tabCounts: params.tabCounts ? true : null,
			},
		}
	)

	return data
}

const STALE_TIME = 3 * 60 * 1000

/**
 * Llave de caché de una consulta del listado.
 *
 * Lleva TODOS los filtros y la página, no un puñado: si algo que viaja en la
 * petición no está aquí, vue-query sirve la respuesta anterior como si fuera la
 * nueva. El `JSON.parse(JSON.stringify())` es a propósito — recorre los valores
 * anidados (`dates`, `creationDates`) para que la llave también cambie cuando se
 * mueve un rango de fechas.
 */
const cefsQueryFilters = (params: Params, page: string | number) =>
	JSON.parse(JSON.stringify({ ...params, page }))

const cefsQueryKey = (params: Params, page: string | number) => [
	'cefs',
	cefsQueryFilters(params, page),
]

const useCompanyCefs = (params: Params, immediate: boolean | Ref<boolean> = true) => {
	const { setCefs, setPagination, setPage } = useCefsStore()
	const { cefs, page, pagination } = storeToRefs(useCefsStore())
	const queryClient = useQueryClient()

	// El computed va DENTRO del arreglo: vue-query desenvuelve las refs de la
	// llave y observa sus cambios, así que al cambiar un filtro pide sola y al
	// volver a una combinación ya vista (la pestaña de la que venías, con el
	// mismo término) responde de caché sin pegarle otra vez al backend.
	const queryFilters = computed(() => cefsQueryFilters(params, page.value))

	const { data, isError, isLoading, refetch, isRefetching, isFetching } =
		useQuery(
			['cefs', queryFilters],
			() =>
				getCompanyCefs(
					{
						...params,
					},
					page.value
				),
			{
				retry: false,
				enabled: immediate,
				staleTime: STALE_TIME,
				keepPreviousData: true,
			}
		)

	watch(
		data,
		(response) => {
			if (response) {
				setCefs(response.data)
				setPagination(_.omit(response, ['data']))
			}
		},
		{ immediate: true }
	)

	/**
	 * Deja lista en caché una variante del listado (los filtros de otra pestaña,
	 * con el mismo término). Cuando el usuario llega ahí, la respuesta ya está y
	 * no se dispara una segunda petición.
	 *
	 * Siempre página 1: es donde cae quien cambia de pestaña.
	 */
	const prefetch = (overrides: Partial<Params>) => {
		const merged = { ...params, ...overrides }

		return queryClient.prefetchQuery(
			cefsQueryKey(merged, 1),
			() => getCompanyCefs(merged, 1),
			{ staleTime: STALE_TIME }
		)
	}

	return {
		data,
		cefs,
		pagination,
		page,
		isError,
		isLoading,
		isRefetching,
		isFetching,

		setPage,
		prefetch,
		searchParams() {
			refetch()
		},
	}
}

export default useCompanyCefs
